import { Link } from '../components/SmartLink';
import { motion, AnimatePresence } from 'motion/react';
import {
  AirplaneTilt, IdentificationCard, Compass, SuitcaseRolling, Buildings, Mosque,
  ShieldCheck, Star, Clock, ArrowRight, MapPin,
  WhatsappLogo, Phone, Headset,
  CaretLeft, CaretRight, Quotes,
  Certificate, Gear, Users, CurrencyDollar,
  GlobeHemisphereEast, FileText, Lightning,
} from '@phosphor-icons/react';
import { useLoaderData } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { AGENCY_SCHEMA, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA } from '../lib/seoSchemas';
import LeadForm from '../components/LeadForm';
import TrustSignals from '../components/TrustSignals';
import { CTASection } from '../components/design-system';
import { buildWAUrl, TEL, WA_BASE } from '../lib/whatsapp';
import { PACKAGES as HOLIDAY_PACKAGES } from '../data/tourPackagesData';
import type { Package, PackageCategory } from '../data/tourPackagesData';
import { useTourPackages, type SanityTourPackage } from '../hooks/useTourPackages';
import { urlFor } from '../lib/urlFor';
import TrustpilotWidget from '../components/TrustpilotWidget';
import { BLOG_POSTS, CATEGORIES, heroFor } from '../data/blogPosts';

type HomeLoaderData = {
  packages?: SanityTourPackage[];
};

// --- Constants ----------------------------------------------------------------


const SERVICES = [
  {
    icon: IdentificationCard,
    title: 'Visa Assistance',
    desc: 'UAE visit visa and international visa assistance for Schengen, UK, USA, Canada, and other destinations.',
    href: '/visas',
    iconBg: '#FAECE7', iconColor: '#993C1D',
    tags: ['Visit & Tourist Visas', 'Document Support'],
    wa: buildWAUrl('Hi Big Bird Travel, I need help with a visa application.'),
  },
  {
    icon: AirplaneTilt,
    title: 'Air Ticket Booking',
    desc: 'Get available flight options, route guidance, baggage information, and booking support from the UAE.',
    href: '/flights',
    iconBg: '#EAF7FF', iconColor: '#0077CC',
    tags: ['Best Fare Search', 'Flexible Ticketing'],
    wa: buildWAUrl('Hi Big Bird Travel, I need help booking flights.'),
  },
  {
    icon: SuitcaseRolling,
    title: 'Holiday Packages',
    desc: 'Customized family, honeymoon, and short-break holiday packages from the UAE.',
    href: '/tour-packages',
    iconBg: '#E1F5EE', iconColor: '#0F6E56',
    tags: ['Family Holidays', 'Custom Itineraries'],
    wa: buildWAUrl('Hi Big Bird Travel, I am interested in booking a holiday package.'),
  },
  {
    icon: Compass,
    title: 'UAE Tours',
    desc: 'Dubai City Tour, Abu Dhabi City Tour, Desert Safari, Dhow Cruise, and family-friendly UAE experiences.',
    href: '/uae-tours',
    iconBg: '#FAEEDA', iconColor: '#854F0B',
    tags: ['Dubai & Abu Dhabi', 'Attraction Tours'],
    wa: buildWAUrl('Hi Big Bird Travel, I want to book a UAE tour or activity.'),
  },
  {
    icon: Buildings,
    title: 'Hotel Booking',
    desc: 'Hotel booking support for local UAE stays and international travel.',
    href: '/hotels',
    iconBg: '#EAF7FF', iconColor: '#0077CC',
    tags: ['Local & Global Hotels', 'Best Stay Options'],
    wa: buildWAUrl('Hi Big Bird Travel, I need help booking a hotel.'),
  },
  {
    icon: Mosque,
    title: 'Umrah Packages',
    desc: 'Umrah travel assistance with package options, hotel support, and travel guidance.',
    href: '/umrah',
    iconBg: '#F5EDFF', iconColor: '#6B3FA0',
    tags: ['Umrah Travel', 'Visa + Hotel Support'],
    wa: buildWAUrl('Hi Big Bird Travel, I am interested in an Umrah package.'),
  },
];

const VISAS = [
  {
    flag: 'AE', country: 'UAE Visit Visa',
    processing: '24-48 hours',
    note: 'Tourist, transit & multi-entry options',
    href: '/visas',
    waText: 'Hi%2C%20I%20need%20help%20with%20UAE%20Visit%20Visa',
  },
  {
    flag: 'EU', country: 'Schengen Visa',
    processing: '5-10 working days',
    note: 'Covers all 27 Schengen countries',
    href: '/visas',
    waText: 'Hi%2C%20I%20need%20Schengen%20Visa%20assistance',
  },
  {
    flag: 'UK', country: 'UK Visa',
    processing: '15-21 working days',
    note: 'Visitor, student & business visa',
    href: '/visas',
    waText: 'Hi%2C%20I%20need%20UK%20Visa%20help',
  },
  {
    flag: 'US', country: 'USA Visa',
    processing: 'Varies by interview date',
    note: 'Tourist B1/B2, DS-160 & interview prep',
    href: '/visas',
    waText: 'Hi%2C%20I%20need%20USA%20Visa%20help',
  },
  {
    flag: 'CA', country: 'Canada Visa',
    processing: '20-30 working days',
    note: 'Visitor TRV & eTA processing',
    href: '/visas',
    waText: 'Hi%2C%20I%20need%20Canada%20Visa%20help',
  },
  {
    flag: 'AU', country: 'Australia Visa',
    processing: '5-14 working days',
    note: 'Tourist eVisa & fast online processing',
    href: '/visas',
    waText: 'Hi%2C%20I%20need%20Australia%20Visa%20help',
  },
];

const VISA_VISUALS = [
  { flagSrc: '/flags/ae.svg', code: 'AE', alt: 'United Arab Emirates flag' },
  { flagSrc: '/flags/eu.svg', code: 'EU', alt: 'European Union flag' },
  { flagSrc: '/flags/gb.svg', code: 'UK', alt: 'United Kingdom flag' },
  { flagSrc: '/flags/us.svg', code: 'US', alt: 'United States flag' },
  { flagSrc: '/flags/ca.svg', code: 'CA', alt: 'Canada flag' },
  { flagSrc: '/flags/au.svg', code: 'AU', alt: 'Australia flag' },
];

const PACKAGES = [
  {
    name: 'Dubai City Tour',
    tag: 'UAE Tour',
    duration: '4-5 Hours',
    price: 'Get Quote',
    desc: 'Dubai landmarks, Jumeirah, Marina views, and modern attractions.',
    img: 'https://images.unsplash.com/photo-1583948591226-8b40a79669dc?auto=format&fit=crop&w=600&q=80&fm=webp',
    href: '/uae-tours',
    waText: encodeURIComponent('Hi Big Bird Travel, I want to book the Dubai City Tour. Please share availability and price.'),
  },
  {
    name: 'Abu Dhabi City Tour',
    tag: 'UAE Tour',
    duration: '8 Hours',
    price: 'Get Quote',
    desc: 'Sheikh Zayed Grand Mosque, Corniche, Emirates Palace area, and cultural highlights.',
    img: 'https://images.unsplash.com/photo-1512632578888-169bbbc64f33?auto=format&fit=crop&w=600&q=80&fm=webp',
    href: '/uae-tours',
    waText: encodeURIComponent('Hi Big Bird Travel, I want to book the Abu Dhabi City Tour. Please share availability and price.'),
  },
  {
    name: 'Desert Safari',
    tag: 'Adventure',
    duration: '6 Hours',
    price: 'Get Quote',
    desc: 'Dune bashing, BBQ dinner, entertainment, and family-friendly safari options.',
    img: '/images/desert-safari-dune-bashing.jpg',
    href: '/uae-tours',
    waText: encodeURIComponent('Hi Big Bird Travel, I want to book the Desert Safari. Please share availability and price.'),
  },
  {
    name: 'Dhow Cruise Dinner',
    tag: 'Cruise',
    duration: '2.5 Hours',
    price: 'Get Quote',
    desc: 'Dinner cruise in Dubai Marina or Creek with beautiful evening views.',
    img: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=600&q=80&fm=webp',
    href: '/uae-tours',
    waText: encodeURIComponent('Hi Big Bird Travel, I want to book the Dhow Cruise Dinner. Please share availability and price.'),
  },
  {
    name: 'UAE Visit Visa',
    tag: 'Visa',
    duration: 'Processed in 24-48 hrs',
    price: 'Apply Now',
    desc: '1 month and 2 months UAE visit visa assistance with document guidance.',
    img: 'https://images.unsplash.com/photo-1544644181-1484b3fdfc62?auto=format&fit=crop&w=600&q=80&fm=webp',
    href: '/visas',
    waText: encodeURIComponent('Hi Big Bird Travel, I need help with a UAE Visit Visa application. Please guide me.'),
  },
  {
    name: 'Holiday Packages from UAE',
    tag: 'Family',
    duration: 'Customized',
    price: 'Plan My Trip',
    desc: 'Customized family, honeymoon, and short-break holiday packages from the UAE.',
    img: 'https://images.unsplash.com/photo-1540151447144-5ee4c23abc57?auto=format&fit=crop&w=600&q=80&fm=webp',
    href: '/tour-packages',
    waText: encodeURIComponent('Hi Big Bird Travel, I am interested in planning a holiday package from UAE. Please share options.'),
  },
];

const TAG_COLORS: Record<string, string> = {
  Honeymoon: 'bg-rose-100 text-rose-700',
  Adventure: 'bg-emerald-100 text-emerald-700',
  Family:    'bg-sky-100 text-sky-700',
  Cultural:  'bg-purple-100 text-purple-700',
  Spiritual: 'bg-amber-100 text-amber-800',
  Budget:    'bg-gray-100 text-gray-700',
  Luxury:    'bg-yellow-100 text-yellow-800',
};

const UAE_TOURS = [
  { name: 'Desert Safari',     sub: 'Dunes, BBQ & camel ride',          img: '/images/desert-safari-dune-bashing.jpg',          href: '/uae-tours' },
  { name: 'Dhow Cruise',       sub: 'Dubai Creek evening cruise',        img: '/images/dhow-cruise-dubai-marina-skyline.jpg',    href: '/uae-tours' },
  { name: 'Yacht Rental',      sub: 'Palm Jumeirah & Marina',            img: '/images/luxury-yacht-dubai-marina.jpg',           href: '/uae-tours' },
  { name: 'Dubai City Tour',   sub: 'Landmarks, Marina & Jumeirah',      img: '/images/dubai-city-tour-skyline.jpg',                                                           href: '/uae-tours' },
  { name: 'Abu Dhabi City Tour', sub: 'Sheikh Zayed Mosque & Corniche', img: 'https://images.unsplash.com/photo-1512632578888-169bbbc64f33?auto=format&fit=crop&w=600&q=80&fm=webp', href: '/uae-tours' },
];

const WHY_ITEMS = [
  { icon: MapPin,          title: 'UAE-Based Consultants',   desc: 'Walk-in office in Mussafah 13, Abu Dhabi. Talk to a real expert in person or via WhatsApp.' },
  { icon: WhatsappLogo,    title: 'Fast WhatsApp Response',  desc: 'WhatsApp enquiries answered quickly by our UAE-based team.' },
  { icon: Certificate,     title: 'Document Review Support', desc: 'We review documents carefully to reduce common application mistakes.' },
  { icon: Gear,            title: 'End-to-End Planning',     desc: 'One team handles your visa, flights, hotels, insurance and activities.' },
  { icon: CurrencyDollar,  title: 'Transparent Guidance',    desc: 'Clear fee guidance and competitive options before you confirm.' },
  { icon: Headset,         title: 'Support During Travel',   desc: 'Our team is reachable throughout your trip for any last-minute changes.' },
];

const STATS: { label: string; desc: string }[] = [
  { label: 'Visa & Travel Support', desc: 'Guidance for visas, flights, hotels, tours, and Umrah.' },
  { label: 'UAE-Based Assistance', desc: 'A real local team you can reach on WhatsApp.' },
  { label: 'Transparent Guidance', desc: 'Clear requirements, fees, and next steps before you confirm.' },
  { label: 'Experienced Travel Team', desc: 'Practical support for families, groups, and business travellers.' },
];

// Real Google reviews from our Google Business Profile (4.7 ★, 29 reviews).
const TESTIMONIALS = [
  { name: 'Crown Knowledge',      service: 'Visa & Holiday Booking', location: 'Local Guide · Google review', stars: 5, avatar: 'C', text: "Excellent service and a stress-free experience! The team was incredibly helpful and guided me through the entire process seamlessly. Their customer support via WhatsApp was fast, polite, and always available to answer my questions. Everything was organized perfectly, and I didn't have to worry about a single detail. Highly recommend their services to anyone looking for reliable travel agents in the UAE!" },
  { name: 'Shadab Taj',           service: 'Corporate Travel',       location: 'Google review', stars: 5, avatar: 'S', text: 'I highly recommend Big Bird Travel for their excellent service and professionalism. They always provide high-quality support, reasonable prices, and reliable assistance. I truly appreciate their quick response and the way they arrange bookings for our guests on time. They have been very cooperative with our company, and we look forward to continuing our successful business relationship.' },
  { name: 'muhammad shazad khan', service: 'Family Visit Visa',      location: 'Google review', stars: 5, avatar: 'M', text: 'I got my family visit visa through this agency and they guided me properly and assisted me to get the visit visa with an affordable price range. I highly recommend them to anyone looking for visit visa services.' },
  { name: 'Akorli Akon',          service: 'Travel Insurance',       location: 'Google review', stars: 5, avatar: 'A', text: 'First class services for the Georgia travel insurance. Got a better price and a good plan.' },
  { name: 'Arther Xavier',        service: 'Travel Services',        location: 'Google review', stars: 5, avatar: 'A', text: 'Great experience with you guys, thanks for the detailed information and kind services.' },
  { name: 'Sahadat Ansari',       service: 'Flight Booking',         location: 'Google review', stars: 5, avatar: 'S', text: 'Book my tickets every time from Big Bird Travel. Nice staff.' },
];

// Latest blog post from each category (newest first within a category), in the
// CATEGORIES display order. Drives the rotating "Travel Guides" section so the
// home page always surfaces fresh, diverse posts without manual edits.
const CAT_LABEL: Record<string, string> = Object.fromEntries(CATEGORIES.map(c => [c.id, c.label]));

const GUIDES = CATEGORIES
  .filter(c => c.id !== 'all')
  .map(c => {
    const latest = BLOG_POSTS
      .filter(p => p.category === c.id)
      .sort((a, b) => b.publishDate.localeCompare(a.publishDate))[0];
    return latest
      ? { title: latest.title, cat: CAT_LABEL[latest.category], img: heroFor(latest), slug: latest.slug, href: undefined as string | undefined }
      : null;
  })
  .filter((g): g is { title: string; cat: string; img: string; slug: string; href: string | undefined } => !!g);

const SERVICE_OPTIONS = [
  'Holiday Package', 'Umrah Package', 'UAE Visit Visa', 'Schengen Visa',
  'UK Visa', 'USA Visa', 'Canada Visa', 'Australia Visa',
  'Flight Tickets', 'Hotel Booking', 'UAE Tours & Activities',
  'Travel Insurance', 'Corporate Travel', 'Other',
];

const HOLIDAY_THEME_TO_CATEGORY: Record<string, PackageCategory> = {
  honeymoon: 'honeymoon',
  family: 'family',
  beach: 'beach',
  short: 'short',
  spiritual: 'spiritual',
  luxury: 'honeymoon',
  adventure: 'short',
  budget: 'short',
  group: 'family',
};

const HOLIDAY_BADGE_STYLES: Record<Package['badgeColor'], string> = {
  coral: 'bg-[#FFF1EE] text-[#E04F3A]',
  gold: 'bg-[#FFF7DF] text-[#A66A00]',
  teal: 'bg-[#E7FFF8] text-[#087A68]',
  navy: 'bg-[#EAF2FF] text-[#0B3D75]',
  green: 'bg-[#EAFBF1] text-[#0C8A45]',
};

function sanityToHolidayPackage(sp: SanityTourPackage): Package {
  return {
    id: sp.slug.current,
    title: sp.title,
    badge: sp.is_featured ? 'Featured' : sp.destination,
    badgeColor: sp.is_featured ? 'coral' : 'navy',
    location: sp.destination,
    nights: sp.duration_nights,
    days: sp.duration_days,
    description: sp.description ?? `${sp.duration_nights} nights in ${sp.destination} with custom travel support.`,
    highlights: sp.highlights?.slice(0, 3) ?? sp.inclusions?.slice(0, 3) ?? ['Flights + hotel', 'Custom itinerary', 'WhatsApp support'],
    priceFromAED: sp.price_per_person_aed,
    image: sp.hero_image ? urlFor(sp.hero_image).width(900).height(520).auto('format').url() : HOLIDAY_PACKAGES[0].image,
    imageAlt: (sp.hero_image as { alt?: string })?.alt ?? `${sp.title} holiday package`,
    categories: (sp.theme ?? []).map(theme => HOLIDAY_THEME_TO_CATEGORY[theme]).filter(Boolean) as PackageCategory[],
    waText: `Hello Big Bird Travel, I want to inquire about the ${sp.title}. Please share details and current pricing.`,
  };
}

function formatHolidayPrice(price?: number) {
  return typeof price === 'number' && Number.isFinite(price)
    ? `AED ${price.toLocaleString()}`
    : 'Custom Quote';
}

// --- Stat Counter -------------------------------------------------------------

function StatCounter({ label, desc }: { label: string; desc: string }) {
  return (
    <div className="text-center">
      <div className="font-display text-xl font-black text-white md:text-2xl">
        {label}
      </div>
      <div className="mx-auto mt-2 max-w-[240px] text-sm font-semibold leading-relaxed text-white/80">{desc}</div>
    </div>
  );
}

// --- Main Component ------------------------------------------------------------

export default function Home() {
  const loaderData = useLoaderData() as HomeLoaderData | undefined;
  const initialSanityPackages = loaderData?.packages ?? [];
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  // Rotating "Travel Guides" window: cycles through the latest post per category.
  const GUIDE_WINDOW = 4;
  const [guideStart, setGuideStart] = useState(0);
  useEffect(() => {
    if (GUIDES.length <= GUIDE_WINDOW) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setGuideStart(s => (s + 1) % GUIDES.length), 4500);
    return () => clearInterval(id);
  }, []);
  const visibleGuides = GUIDES.length <= GUIDE_WINDOW
    ? GUIDES
    : Array.from({ length: GUIDE_WINDOW }, (_, i) => GUIDES[(guideStart + i) % GUIDES.length]);
  const [quote, setQuote] = useState({ service: '', date: '', travellers: '2', whatsapp: '' });
  const { packages: sanityPackages } = useTourPackages(initialSanityPackages);

  const prevT = () => setTestimonialIdx(i => (i - 3 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const nextT = () => setTestimonialIdx(i => (i + 3) % TESTIMONIALS.length);
  const visibleT = [0, 1, 2].map(o => TESTIMONIALS[(testimonialIdx + o) % TESTIMONIALS.length]);
  const holidayPackages = (sanityPackages.length > 0 ? sanityPackages.map(sanityToHolidayPackage) : HOLIDAY_PACKAGES).slice(0, 5);
  const featuredHolidayPackage = holidayPackages[0] ?? HOLIDAY_PACKAGES[0];
  const sideHolidayPackages = holidayPackages.slice(1, 5);

  const handleQuote = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setQuote(p => ({ ...p, [e.target.name]: e.target.value }));

  const submitQuote = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = `Hi, I'd like a free quote.\nService: ${quote.service}\nTravel Date: ${quote.date}\nTravellers: ${quote.travellers}`;
    window.open(buildWAUrl(msg), '_blank');
  };

  return (
    <div className="bg-white">
      <SEO
        title="Travel Agency in Abu Dhabi | Big Bird Travel & Tourism UAE"
        description="Big Bird Travel is a UAE travel agency in Abu Dhabi (Mussafah) offering visa assistance, air tickets, hotel booking, UAE tours, Umrah packages, and holiday packages."
        canonical="/"
        schema={[AGENCY_SCHEMA, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA]}
        skipSuffix
      />

      {/* --- HERO ----------------------------------------------------------- */}
      <section className="relative overflow-hidden min-h-[calc(100svh-72px)] lg:min-h-[90vh]">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/hero-dubai-skyline.webp"
            alt="Dubai skyline - Big Bird Travel & Tourism UAE"
            className="w-full h-full object-cover"
            width={1200}
            height={1800}
            fetchPriority="high"
            decoding="async"
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(110deg, #071126 45%, rgba(11,31,58,0.75) 68%, rgba(11,31,58,0.3) 100%)' }}
          />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 flex items-center min-h-[calc(100svh-72px)] lg:min-h-[90vh]">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="w-full max-w-2xl pt-20 pb-10 sm:py-20 lg:py-28"
          >
            {/* Eyebrow */}
            <span className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.24em] sm:tracking-[0.32em] text-[#0068B3] mb-4 sm:mb-5">
              <span className="w-4 h-px bg-[#0096FF]" />
              UAE-Based Travel Agency
            </span>

            {/* H1 */}
            <h1
              className="font-display font-black text-white leading-[1.08] mb-4 sm:mb-5"
              style={{ fontSize: 'clamp(1.85rem, 8vw, 3.15rem)' }}
            >
              UAE Travel Agency for Visas, Air Tickets &amp; Holiday Packages
            </h1>

            {/* Subheadline */}
            <p className="text-white/72 text-[14px] sm:text-[15px] md:text-[16px] leading-relaxed mb-6 sm:mb-8 font-medium max-w-xl">
              Reliable assistance for UAE visit visas, international visa support, flight bookings, hotel bookings, UAE tours, Umrah packages, and holiday packages - all from one UAE-based team.
            </p>

            {/* Trust badges - inline, near the headline */}
            <div className="flex flex-wrap gap-x-4 sm:gap-x-5 gap-y-2 mb-7 sm:mb-9">
              {[
                { icon: MapPin,             label: 'UAE-Based Travel Agency' },
                { icon: IdentificationCard, label: 'Visa & Ticket Assistance' },
                { icon: WhatsappLogo,       label: 'Fast WhatsApp Support' },
                { icon: SuitcaseRolling,    label: 'Holiday Packages from UAE' },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-[11px] sm:text-[12px] font-semibold text-white/75">
                  <Icon size={13} weight="fill" className="text-[#0068B3] shrink-0" />
                  {label}
                </span>
              ))}
            </div>

            {/* Primary + Secondary CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-7 sm:mb-10">
              <a
                href={WA_BASE}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 px-5 sm:px-7 py-3.5 sm:py-4 rounded-xl font-black text-[12px] sm:text-[13px] uppercase tracking-wider text-white transition-all hover:opacity-90 shadow-lg shadow-black/20"
                style={{ background: '#0F7A35' }}
              >
                <WhatsappLogo size={18} weight="fill" /> WhatsApp Us Now
              </a>
              <Link
                to="/tour-packages"
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-3.5 sm:py-4 rounded-xl font-black text-[12px] sm:text-[13px] uppercase tracking-wider text-white border border-white/25 hover:border-white/60 hover:bg-white/10 transition-all"
              >
                Explore Services
              </Link>
            </div>

            {/* Quick Quote Box */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-[560px]">
              <div className="px-4 sm:px-5 pt-4 pb-1">
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-[#0068B3]">Get a Free Quote - No Obligation</p>
              </div>
              <form onSubmit={submitQuote} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select
                  name="service"
                  aria-label="Select destination or service"
                  value={quote.service}
                  onChange={handleQuote}
                  required
                  className="col-span-2 bg-[#F5F5F5] rounded-xl px-4 py-3 text-[#071126] font-medium text-[13px] outline-none focus:ring-2 focus:ring-[#0096FF]"
                >
                  <option value="">Select destination or service...</option>
                  {SERVICE_OPTIONS.map(s => <option key={s}>{s}</option>)}
                </select>
                <input
                  name="date"
                  type="date"
                  aria-label="Travel date"
                  value={quote.date}
                  onChange={handleQuote}
                  className="bg-[#F5F5F5] rounded-xl px-4 py-3 text-[#071126] font-medium text-[13px] outline-none focus:ring-2 focus:ring-[#0096FF]"
                />
                <select
                  name="travellers"
                  aria-label="Number of travellers"
                  value={quote.travellers}
                  onChange={handleQuote}
                  className="bg-[#F5F5F5] rounded-xl px-4 py-3 text-[#071126] font-medium text-[13px] outline-none focus:ring-2 focus:ring-[#0096FF]"
                >
                  {['1', '2', '3', '4', '5', '6', '7', '8+'].map(n => (
                    <option key={n} value={n}>{n} Traveller{n !== '1' ? 's' : ''}</option>
                  ))}
                </select>
                <input
                  name="whatsapp"
                  type="tel"
                  aria-label="Your WhatsApp number"
                  placeholder="Your WhatsApp number"
                  value={quote.whatsapp}
                  onChange={handleQuote}
                  className="col-span-2 bg-[#F5F5F5] rounded-xl px-4 py-3 text-[#071126] font-medium text-[13px] outline-none focus:ring-2 focus:ring-[#0096FF] placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  className="col-span-2 bg-[#0077CC] text-white py-3.5 rounded-xl font-black text-[12px] uppercase tracking-wider hover:bg-[#071126] transition-colors flex items-center justify-center gap-2"
                >
                  <WhatsappLogo size={16} weight="fill" /> Get Free Quote via WhatsApp
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- TRUST STRIP --------------------------------------------------- */}
      <div className="bg-[#071126] border-t border-white/8 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-6 md:gap-10">
          {[
            { icon: MapPin,             text: 'UAE-Based Travel Agency' },
            { icon: IdentificationCard, text: 'Visa & Ticket Assistance' },
            { icon: WhatsappLogo,       text: 'Fast WhatsApp Support' },
            { icon: SuitcaseRolling,    text: 'Holiday Packages from UAE' },
            { icon: ShieldCheck,        text: 'Trade Licensed' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="flex items-center gap-2">
                <Icon size={14} weight="fill" className="text-[#0068B3] shrink-0" />
                <span className="text-[11px] font-semibold text-white/75 whitespace-nowrap">{item.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- SERVICES ------------------------------------------------------- */}
      <section className="relative overflow-hidden px-5 py-10 sm:px-6 lg:px-8 lg:py-12" style={{ background: 'linear-gradient(180deg, #F7FBFF 0%, #FFFFFF 50%, #EFF8FF 100%)' }}>
        <img
          src="/assets/backgrounds/decorative_flight_path_illustration.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -left-28 top-0 hidden w-[360px] opacity-45 md:block" loading="lazy" />
        <img
          src="/assets/backgrounds/blue_airplane_and_flight_path.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 top-10 hidden w-[420px] opacity-35 lg:block" loading="lazy" />
        <img
          src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 w-full opacity-45" loading="lazy" />
        <div className="relative z-10 mx-auto max-w-[1480px]">
          <div className="mx-auto mb-6 max-w-3xl text-center">
            <div className="mb-2 flex items-center justify-center gap-4">
              <span className="hidden h-px w-16 bg-[#0096FF]/30 sm:block" />
              <span className="text-[11px] font-black uppercase tracking-[0.34em] text-[#0068B3]">
                Our Services
              </span>
              <span className="hidden h-px w-16 bg-[#0096FF]/30 sm:block" />
            </div>
            <h2 className="font-display font-black leading-[1.05] text-[#071126]" style={{ fontSize: 'clamp(1.75rem, 3.2vw, 2.7rem)' }}>
              What We Can Help You With
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-[14px] font-medium leading-relaxed text-[#516070] sm:text-[15px]">
              From visa applications and air ticket booking to hotel reservations, UAE tours, Umrah packages, and holiday packages, our UAE-based team helps you plan every part of your journey.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mx-auto grid max-w-[1120px] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
          >
            {SERVICES.map((svc, idx) => {
              const Icon = svc.icon;
              return (
                <article
                  key={svc.title}
                  className="group flex h-full flex-col rounded-[20px] border border-[#CFE6FA] bg-white/95 p-4 shadow-[0_14px_35px_rgba(7,17,38,0.07)] backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-[#0096FF]/45 hover:shadow-[0_20px_48px_rgba(0,150,255,0.13)] sm:p-5"
                >
                  <div className="mb-3 flex items-start gap-4">
                    <span
                      className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-2xl"
                      style={{ background: svc.iconBg }}
                    >
                      <Icon size={30} weight="duotone" style={{ color: svc.iconColor }} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-display text-[18px] font-black leading-tight text-[#071126] transition-colors group-hover:text-[#0068B3]">
                        {svc.title}
                      </h3>
                      <p className="mt-2 text-[13px] font-medium leading-relaxed text-[#435166]">
                        {svc.desc}
                      </p>
                    </div>
                  </div>

                  <div className="mt-auto">
                    <div className="mb-2 flex flex-wrap gap-2">
                      {svc.tags.map(tag => (
                        <span
                          key={tag}
                          className="rounded-lg border border-[#0096FF]/55 bg-white px-2.5 py-1 text-[10.5px] font-black text-[#0068B3]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <a
                      href={svc.wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#16B85E] py-2.5 text-[13px] font-black text-white shadow-lg shadow-[#16B85E]/20 transition-all hover:bg-[#0EA451]"
                    >
                      <WhatsappLogo size={16} weight="fill" /> Get Quote on WhatsApp
                    </a>
                    <Link
                      to={svc.href}
                      className="mt-2 flex items-center justify-center gap-2 text-[12px] font-black text-[#0068B3] transition-all hover:gap-3 hover:text-[#0068B3]"
                    >
                      More on {svc.title} <ArrowRight size={15} weight="bold" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </motion.div>

          <div className="mx-auto mt-4 grid max-w-[1040px] grid-cols-1 gap-3 rounded-[20px] border border-[#CFE6FA] bg-white/90 px-5 py-3 shadow-[0_12px_32px_rgba(7,17,38,0.07)] backdrop-blur sm:grid-cols-2 lg:grid-cols-4 lg:px-7">
            {[
              { icon: Users, title: 'UAE-Based Team', text: 'Local experts, better support' },
              { icon: WhatsappLogo, title: 'Fast WhatsApp Response', text: 'Quick replies, real humans' },
              { icon: ShieldCheck, title: 'Transparent Pricing', text: 'No hidden charges' },
              { icon: Headset, title: 'One Team for All Travel Needs', text: 'Visa, flights, hotels & more' },
            ].map(({ icon: Icon, title, text }, idx) => (
              <div key={title} className={`flex items-center gap-3 ${idx > 0 ? 'lg:border-l lg:border-[#CFE6FA] lg:pl-6' : ''}`}>
                <Icon size={28} weight="duotone" className="shrink-0 text-[#0068B3]" />
                <div>
                  <p className="font-display text-[13px] font-black leading-tight text-[#071126]">{title}</p>
                  <p className="mt-0.5 text-[11px] font-medium text-[#516070]">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visa services */}
      <section className="relative overflow-hidden bg-[#F5FBFF] px-5 py-11 lg:px-8 lg:py-12">
        <img
          src="/assets/backgrounds/blue_airplane_and_flight_path.webp"
          alt=""
          className="pointer-events-none absolute -left-16 top-0 hidden w-[420px] max-w-none opacity-[0.16] lg:block"
          aria-hidden="true" loading="lazy" />
        <img
          src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
          alt=""
          className="pointer-events-none absolute -right-10 bottom-0 hidden w-[520px] max-w-none opacity-[0.2] lg:block"
          aria-hidden="true" loading="lazy" />
        <div className="pointer-events-none absolute right-[8%] top-8 hidden h-52 w-52 rounded-full bg-[#0096FF]/10 blur-3xl lg:block" aria-hidden="true" />

        <div className="relative z-10 mx-auto max-w-[1480px]">
          <div className="mb-7 text-center">
            <span className="mb-2 block text-[11px] font-black uppercase tracking-[0.34em] text-[#0068B3]">
              Visa Services
            </span>
            <h2 className="font-display font-black leading-tight text-[#071126]" style={{ fontSize: 'clamp(1.9rem, 3.2vw, 3rem)' }}>
              Visa Assistance Made Simple
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-[14px] font-medium leading-relaxed text-[#40516A] md:text-[16px]">
              Fast document guidance, application support, and expert visa assistance for UAE residents across major travel destinations.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
          >
            {VISAS.map((v, idx) => {
              const visual = VISA_VISUALS[idx] || VISA_VISUALS[0];
              return (
              <div
                key={v.country}
                className="group flex min-h-[255px] flex-col items-center rounded-[18px] border border-[#D6E8F8] bg-white/95 px-4 py-5 text-center shadow-[0_12px_32px_rgba(7,17,38,0.07)] transition-all hover:-translate-y-1 hover:border-[#0096FF]/45 hover:shadow-[0_18px_42px_rgba(0,150,255,0.13)]"
              >
                <div className="relative mb-4 flex h-[66px] w-[66px] items-center justify-center rounded-full border border-[#BFE1FF] bg-[#EAF7FF] p-2 shadow-inner shadow-white">
                  <img
                    src={visual.flagSrc}
                    alt={visual.alt}
                    className="h-10 w-10 rounded-full object-cover shadow-sm ring-1 ring-[#D9EAF7]"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="absolute -bottom-1 -right-1 rounded-full bg-[#0077E6] px-1.5 py-0.5 text-[9px] font-black text-white">
                    {visual.code}
                  </span>
                </div>
                <h3 className="font-display text-[16px] font-black leading-tight text-[#071126] transition-colors group-hover:text-[#0068B3]">
                  {v.country}
                </h3>
                <div className="mt-3 flex items-center gap-1.5 text-[12px] font-black text-[#0077E6]">
                  <Clock size={14} weight="fill" /> {v.processing}
                </div>
                <p className="mt-3 min-h-[38px] text-[13px] font-medium leading-relaxed text-[#5B6A7A]">{v.note}</p>
                <div className="mt-auto flex w-full flex-col gap-2 pt-4">
                  <Link
                    to={v.href}
                    className="flex min-h-10 w-full items-center justify-center rounded-lg bg-[#071126] px-3 text-center text-[12px] font-black text-white transition-colors hover:bg-[#0077CC]"
                  >
                    Check Requirements
                  </Link>
                  <a
                    href={`${WA_BASE}?text=${v.waText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-10 w-full items-center justify-center gap-1.5 rounded-lg text-center text-[12px] font-black text-white shadow-lg shadow-[#25D366]/15 transition-all hover:opacity-90"
                    style={{ background: '#0F7A35' }}
                  >
                    <WhatsappLogo size={15} weight="fill" /> Ask Expert
                  </a>
                </div>
              </div>
              );
            })}
          </motion.div>

          {/* Visa Trust Bar */}
          <div
            className="grid items-center gap-5 rounded-[18px] border border-white/10 px-5 py-5 shadow-[0_18px_42px_rgba(7,17,38,0.14)] md:grid-cols-[1fr_auto] lg:px-8"
            style={{ background: 'linear-gradient(90deg, #071126, #0B2F5B)' }}
          >
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:gap-6">
              {[
                { icon: FileText, v: 'Visa', l: 'Document Review Support' },
                { icon: ShieldCheck, v: 'UAE', l: 'Based Assistance' },
                { icon: GlobeHemisphereEast, v: 'Travel', l: 'Destination Guidance' },
                { icon: Lightning, v: 'Fast', l: 'WhatsApp Response' },
              ].map(({ icon: Icon, v, l }, statIdx) => (
                <div key={l} className={`flex items-center gap-3 ${statIdx > 0 ? 'sm:border-l sm:border-white/15 sm:pl-5' : ''}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#0096FF]/50 bg-[#0096FF]/10 text-[#58B9FF]">
                    <Icon size={23} weight="duotone" />
                  </span>
                  <div>
                    <div className="font-display text-[20px] font-black leading-none text-white">{v}</div>
                    <div className="mt-1 text-[12px] font-semibold leading-tight text-white/75">{l}</div>
                  </div>
                </div>
              ))}
            </div>
            <a
              href={`${WA_BASE}?text=Hi%2C%20I%20need%20visa%20assistance`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-12 items-center justify-center gap-2 rounded-xl px-6 text-[14px] font-black text-white shadow-lg shadow-[#25D366]/20 transition-all hover:opacity-90 md:min-w-[270px]"
              style={{ background: '#0F7A35' }}
            >
              <WhatsappLogo size={16} weight="fill" /> Speak to a Visa Expert
            </a>
          </div>
        </div>
      </section>

      {/* --- HOLIDAY PACKAGES PREVIEW --------------------- */}
      <section className="relative overflow-hidden bg-[#F5FBFF] px-5 py-12 lg:px-8 lg:py-14">
        <img
          src="/assets/backgrounds/blue_airplane_and_flight_path.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 top-8 hidden w-[470px] max-w-none opacity-[0.1] lg:block" loading="lazy" />
        <img
          src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-20 bottom-0 hidden w-[340px] max-w-none opacity-[0.13] xl:block" loading="lazy" />
        <img
          src="/assets/backgrounds/blue_map_pin_icon_on_transparency.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -left-12 bottom-20 hidden w-[160px] max-w-none opacity-[0.11] lg:block" loading="lazy" />

        <div className="relative z-10 mx-auto max-w-[1500px]">
          <div className="mb-6 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-[#0068B3]">
                <span className="h-2.5 w-2.5 rounded-full bg-[#76B9FF]" />
                Holiday Packages
              </span>
              <h2 className="font-display font-black leading-tight text-[#071126]" style={{ fontSize: 'clamp(1.75rem, 3.2vw, 2.9rem)' }}>
                Holiday packages from the UAE.
              </h2>
              <p className="mt-3 max-w-2xl text-[14px] font-medium leading-relaxed text-[#40516A]">
                From tropical beaches to vibrant cities and scenic escapes, explore handpicked holiday packages from the UAE with flexible options for families, couples, and solo travellers.
              </p>
            </div>
            <Link
              to="/tour-packages"
              className="inline-flex w-fit items-center gap-2 rounded-xl border-2 border-[#0096FF] px-5 py-3 text-[12px] font-black text-[#006ED3] transition-all hover:bg-[#0077CC] hover:text-white"
            >
              View All Packages <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.08fr_1.45fr]">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group overflow-hidden rounded-[20px] border border-[#D7E9F8] bg-white shadow-[0_14px_36px_rgba(7,17,38,0.08)] transition-all hover:-translate-y-1 hover:shadow-[0_22px_52px_rgba(0,150,255,0.14)]"
            >
              <div className="relative h-[250px] overflow-hidden lg:h-[300px]">
                <img
                  src={featuredHolidayPackage.image}
                  alt={featuredHolidayPackage.imageAlt}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/35 to-transparent" />
                <span className={`absolute left-4 top-4 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wider shadow-sm ${HOLIDAY_BADGE_STYLES[featuredHolidayPackage.badgeColor]}`}>
                  {featuredHolidayPackage.badge}
                </span>
              </div>
              <div className="p-5">
                <p className="mb-2 flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-[#0068B3]">
                  <MapPin size={14} weight="fill" />
                  {featuredHolidayPackage.location}
                </p>
                <h3 className="font-display text-[24px] font-black leading-tight text-[#071126] transition-colors group-hover:text-[#0068B3]">
                  {featuredHolidayPackage.title}
                </h3>
                <p className="mt-2 text-[14px] font-medium leading-relaxed text-[#40516A]">
                  {featuredHolidayPackage.description}
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2 border-y border-[#E4F1FA] py-3 text-[12px] font-bold text-[#5B6B82] sm:grid-cols-3">
                  <span className="flex items-center gap-1.5"><Clock size={14} weight="duotone" className="text-[#0068B3]" /> {featuredHolidayPackage.days} Days / {featuredHolidayPackage.nights} Nights</span>
                  <span className="flex items-center gap-1.5"><Star size={14} weight="fill" className="text-[#006ED3]" /> 4* Hotels</span>
                  <span className="flex items-center gap-1.5"><AirplaneTilt size={14} weight="duotone" className="text-[#0068B3]" /> Flights + Hotel</span>
                </div>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-[12px] font-semibold text-[#5B6B82]">
                    Starting from <span className="font-display text-[22px] font-black text-[#0068B3]">{formatHolidayPrice(featuredHolidayPackage.priceFromAED)}</span> / per person
                  </div>
                  <Link to={`/tour-packages/${featuredHolidayPackage.id}`} className="inline-flex items-center gap-1.5 text-[12px] font-black text-[#071126] hover:text-[#0068B3]">
                    View details <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
                <a
                  href={buildWAUrl(featuredHolidayPackage.waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl text-[13px] font-black text-white shadow-lg shadow-[#25D366]/15 transition-all hover:opacity-90 sm:w-fit sm:px-7"
                  style={{ background: '#0F7A35' }}
                >
                  <WhatsappLogo size={17} weight="fill" /> Get Quote on WhatsApp
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 gap-5 md:grid-cols-2"
            >
              {sideHolidayPackages.map((pkg) => (
              <div
                key={pkg.id}
                className="group overflow-hidden rounded-[18px] border border-[#D7E9F8] bg-white shadow-[0_14px_36px_rgba(7,17,38,0.08)] transition-all hover:-translate-y-1 hover:shadow-[0_22px_52px_rgba(0,150,255,0.14)]"
              >
                <div className="relative h-[132px] overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.imageAlt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/35 to-transparent" />
                  <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[9px] font-black uppercase tracking-wider shadow-sm ${HOLIDAY_BADGE_STYLES[pkg.badgeColor]}`}>
                    {pkg.location}
                  </span>
                </div>
                <div className="flex min-h-[230px] flex-col p-4">
                  <h3 className="font-display text-[18px] font-black leading-tight text-[#071126] transition-colors group-hover:text-[#0068B3]">
                    {pkg.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-[13px] font-medium leading-relaxed text-[#40516A]">
                    {pkg.description}
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-bold text-[#5B6B82]">
                    <span className="flex items-center gap-1.5 rounded-lg bg-[#F3F8FC] px-2 py-2"><Clock size={13} weight="duotone" className="text-[#0068B3]" /> {pkg.days}D / {pkg.nights}N</span>
                    <span className="flex items-center gap-1.5 rounded-lg bg-[#F3F8FC] px-2 py-2"><Star size={13} weight="fill" className="text-[#006ED3]" /> 4* Hotels</span>
                  </div>
                  <div className="mt-auto flex items-center justify-between gap-3 pt-4">
                    <span className="text-[12px] font-semibold text-[#5B6B82]">From <span className="font-display text-[18px] font-black text-[#0068B3]">{formatHolidayPrice(pkg.priceFromAED)}</span></span>
                    <Link to={`/tour-packages/${pkg.id}`} className="inline-flex items-center gap-1.5 text-[12px] font-black text-[#006ED3] hover:text-[#0068B3]">
                      View Details <ArrowRight size={13} weight="bold" />
                    </Link>
                  </div>
                  <a
                    href={buildWAUrl(pkg.waText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl text-[12px] font-black text-white shadow-lg shadow-[#25D366]/15 transition-all hover:opacity-90"
                    style={{ background: '#0F7A35' }}
                  >
                    <WhatsappLogo size={15} weight="fill" /> Get Quote on WhatsApp
                  </a>
                </div>
              </div>
              ))}
            </motion.div>
          </div>

          <div className="mt-7 grid gap-3 rounded-2xl border border-[#D7E9F8] bg-white/90 p-4 shadow-[0_12px_34px_rgba(7,17,38,0.06)] md:grid-cols-4">
            {[
              { icon: AirplaneTilt, title: 'Flights + Hotel', text: 'Best deals, all in one place' },
              { icon: FileText, title: 'Visa Guidance', text: 'Expert help for a hassle-free trip' },
              { icon: Users, title: 'Family-Friendly Options', text: 'Packages for every kind of traveller' },
              { icon: ShieldCheck, title: 'Custom Itineraries', text: 'Tailored to your preferences' },
            ].map(({ icon: Icon, title, text }, idx) => (
              <div key={title} className={`flex items-center gap-3 ${idx > 0 ? 'md:border-l md:border-[#D7E9F8] md:pl-5' : ''}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#EAF7FF] text-[#0068B3]">
                  <Icon size={24} weight="duotone" />
                </span>
                <span>
                  <span className="block font-display text-[14px] font-black text-[#071126]">{title}</span>
                  <span className="block text-[11px] font-semibold text-[#5B6B82]">{text}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- UAE TOURS BENTO ------------------------------------------------ */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
            <div>
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">
                Explore Dubai
              </span>
              <h2 className="font-display font-black text-[#071126]" style={{ fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)' }}>
                UAE tours & activities.
              </h2>
            </div>
            <Link
              to="/uae-tours"
              className="inline-flex items-center gap-2 border-2 border-[#0096FF] text-[#0068B3] px-5 py-3 rounded-xl font-black text-[11px] uppercase tracking-wider hover:bg-[#0077CC] hover:text-white transition-all whitespace-nowrap"
            >
              All Activities <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to={UAE_TOURS[0].href}
              className="relative rounded-2xl overflow-hidden group"
              style={{ minHeight: 400 }}
            >
              <img src={UAE_TOURS[0].img} alt={UAE_TOURS[0].name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/85 via-[#071126]/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div className="text-white">
                  <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#A8D8F0] mb-1">{UAE_TOURS[0].sub}</p>
                  <h3 className="font-display font-black text-2xl">{UAE_TOURS[0].name}</h3>
                </div>
                <a
                  href={`${WA_BASE}?text=Hi%2C%20I%27m%20interested%20in%20Desert%20Safari`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-[11px] font-black shrink-0 hover:opacity-90 transition-all"
                  style={{ background: '#0F7A35' }}
                >
                  <WhatsappLogo size={13} weight="fill" /> Book
                </a>
              </div>
            </Link>
            <div className="grid grid-cols-2 gap-4">
              {UAE_TOURS.slice(1).map(tour => (
                <Link
                  key={tour.name}
                  to={tour.href}
                  className="relative rounded-2xl overflow-hidden group"
                  style={{ minHeight: 190 }}
                >
                  <img src={tour.img} alt={tour.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-[#A8D8F0] mb-0.5">{tour.sub}</p>
                    <h3 className="font-display font-black text-[15px]">{tour.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="relative overflow-hidden bg-[#F5FBFF] px-5 py-9 lg:px-8 lg:py-10">
        <img
          src="/assets/backgrounds/blue_airplane_and_flight_path.webp"
          alt=""
          className="pointer-events-none absolute -left-24 top-4 hidden w-[360px] max-w-none opacity-[0.11] lg:block"
          aria-hidden="true" loading="lazy" />
        <img
          src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
          alt=""
          className="pointer-events-none absolute -left-12 bottom-0 hidden w-[320px] max-w-none opacity-[0.14] lg:block"
          aria-hidden="true" loading="lazy" />
        <img
          src="/assets/backgrounds/soft_blue_abstract_splash_shape.webp"
          alt=""
          className="pointer-events-none absolute -right-28 -top-20 hidden w-[360px] max-w-none opacity-[0.14] lg:block"
          aria-hidden="true" loading="lazy" />
        <img
          src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
          alt=""
          className="pointer-events-none absolute -right-20 bottom-0 hidden w-[280px] max-w-none opacity-[0.1] xl:block"
          aria-hidden="true" loading="lazy" />

        <div className="relative z-10 mx-auto max-w-[1400px]">
          <div className="mx-auto mb-6 max-w-3xl text-center">
            <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.32em] text-[#0068B3]">
              Our Promise
            </span>
            <h2 className="font-display font-black leading-tight text-[#071126]" style={{ fontSize: 'clamp(1.65rem, 2.8vw, 2.55rem)' }}>
              Why Choose Big Bird Travel?
            </h2>
            <div className="mx-auto mt-3 h-0.5 w-14 rounded-full bg-[#0096FF]" />
            <p className="mx-auto mt-3 max-w-2xl text-[13px] font-medium leading-relaxed text-[#40516A] md:text-[14px]">
              UAE residents trust Big Bird Travel for expert visa guidance, seamless flight bookings, complete holiday planning, transparent prices, and real human support every step of the way.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
          >
            {WHY_ITEMS.map((item, idx) => {
              const Icon = item.icon;
              const promises = [
                'Local expertise you can rely on',
                'Fast replies. Real people.',
                'Higher approval. Greater peace of mind.',
                'One trip. Everything taken care of.',
                'Fair prices. Always.',
                "We're with you, all the way.",
              ];
              const styles = [
                { bg: '#EAF7FF', color: '#0096FF', ring: '#BFE1FF' },
                { bg: '#EAFBF1', color: '#16B85E', ring: '#BBF0D1' },
                { bg: '#EEF5FF', color: '#2563EB', ring: '#C7DCFF' },
                { bg: '#FFF6E8', color: '#F59E0B', ring: '#FAD9A5' },
                { bg: '#FFF1E8', color: '#EA580C', ring: '#FDBA8C' },
                { bg: '#F1EEFF', color: '#7C3AED', ring: '#D7CCFF' },
              ];
              const style = styles[idx] || styles[0];
              return (
                <div
                  key={item.title}
                  className="group rounded-[16px] border border-[#D7E9F8] bg-white/95 p-4 shadow-[0_10px_28px_rgba(7,17,38,0.06)] transition-all hover:-translate-y-0.5 hover:border-[#0096FF]/40 hover:shadow-[0_16px_36px_rgba(0,150,255,0.11)]"
                >
                  <div className="flex gap-3">
                    <div
                      className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] ring-1"
                      style={{ backgroundColor: style.bg, color: style.color, ['--tw-ring-color' as string]: style.ring }}
                    >
                      <Icon size={27} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-display text-[15px] font-black leading-tight text-[#071126]">{item.title}</h3>
                      <p className="mt-1.5 text-[12px] font-medium leading-relaxed text-[#40516A]">{item.desc}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 border-t border-[#D7E9F8] pt-2.5 text-[11px] font-black" style={{ color: style.color }}>
                    <ShieldCheck size={15} weight="duotone" className="shrink-0" />
                    {promises[idx]}
                  </div>
                </div>
              );
            })}
          </motion.div>

          <div className="mt-4 grid items-center gap-3 rounded-[18px] border border-white/10 px-4 py-3.5 shadow-[0_16px_36px_rgba(7,17,38,0.13)] lg:grid-cols-[1fr_auto]" style={{ background: 'linear-gradient(90deg, #071126, #0B2F5B)' }}>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: ShieldCheck, title: 'Trusted by Thousands', text: 'Happy travellers across the UAE' },
                { icon: Star, title: '4.8 Rated', text: 'On Google by our customers' },
                { icon: Certificate, title: 'Secure & Compliant', text: 'Your data and documents are safe' },
                { icon: Users, title: '100% Human Support', text: 'No bots. Just real people.' },
              ].map(({ icon: Icon, title, text }, idx) => (
                <div key={title} className={`flex items-center gap-3 ${idx > 0 ? 'lg:border-l lg:border-white/15 lg:pl-5' : ''}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[#0068B3]">
                    <Icon size={20} weight="duotone" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-display text-[13px] font-black leading-tight text-white">{title}</p>
                    <p className="mt-0.5 text-[11px] font-medium leading-tight text-white/70">{text}</p>
                  </div>
                </div>
              ))}
            </div>
            <a
              href={WA_BASE}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#0F7A35] px-5 text-[13px] font-black text-white shadow-lg shadow-[#25D366]/20 transition-all hover:bg-[#0EA451]"
            >
              <WhatsappLogo size={18} weight="fill" />
              <span>
                Chat on WhatsApp
                <span className="block text-[11px] font-semibold leading-tight text-white/95">Get a quick response</span>
              </span>
              <ArrowRight size={17} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      {/* --- TRUST SIGNALS ------------------------------------------------- */}
      <TrustSignals bg="bg-white" />

      {/* --- STATS --------------------------------------------------------- */}
      <section
        className="py-20 px-6 lg:px-8"
        style={{ background: 'linear-gradient(135deg, #071126 0%, #15355A 100%)' }}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => <StatCounter key={i} {...s} />)}
        </div>
      </section>

      {/* --- LEAD CAPTURE FORM --------------------------------------------- */}
      <section className="home-consultation-section relative overflow-hidden px-4 py-8 sm:px-6 lg:px-8 lg:py-10" style={{ background: 'linear-gradient(180deg, #F7FCFF 0%, #FFFFFF 56%, #EEF8FF 100%)' }}>
        <img
          src="/assets/backgrounds/blue_airplane_and_flight_path.webp"
          alt=""
          className="pointer-events-none absolute -left-20 top-3 hidden w-[420px] opacity-[0.11] md:block"
          aria-hidden="true" loading="lazy" />
        <img
          src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
          alt=""
          className="pointer-events-none absolute inset-x-0 bottom-0 h-36 w-full object-cover object-bottom opacity-[0.12]"
          aria-hidden="true" loading="lazy" />
        <img
          src="/assets/backgrounds/soft_blue_abstract_splash_shape.webp"
          alt=""
          className="pointer-events-none absolute -right-28 top-10 hidden w-[360px] opacity-[0.16] lg:block"
          aria-hidden="true" loading="lazy" />

        <div className="relative mx-auto max-w-[1320px]">
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-8">

            {/* Left - Copy */}
            <div className="rounded-[26px] border border-[#CFE8FF] bg-white/78 p-5 shadow-[0_18px_55px_rgba(15,23,42,0.08)] backdrop-blur sm:p-6 lg:p-7">
              <span className="mb-3 block text-[10px] font-black uppercase tracking-[0.32em] text-[#0068B3]">
                Free Consultation
              </span>
              <h2 className="mb-3 font-display font-black leading-[1.08] text-[#071126]" style={{ fontSize: 'clamp(1.75rem, 2.65vw, 2.65rem)' }}>
                Tell us your travel plan.
              </h2>
              <p className="mb-5 max-w-2xl text-[14px] font-semibold leading-relaxed text-slate-600 sm:text-[15px]">
                Fill the form and we'll get back to you within 1 hour with a personalised quote - completely free, no obligation.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {[
                  { icon: WhatsappLogo, label: 'WhatsApp', value: '+971 56 178 9642', href: WA_BASE, color: '#25D366' },
                  { icon: Phone, label: 'Call Us', value: '+971 56 178 9642', href: TEL, color: '#0096FF' },
                  { icon: MapPin, label: 'Office', value: 'Mussafah 13, Abu Dhabi', href: '/contact/', color: '#071126' },
                ].map(c => {
                  const Icon = c.icon;
                  return (
                    <a
                      key={c.label}
                      href={c.href}
                      target={c.href.startsWith('http') ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className="group flex min-w-0 items-center gap-3 rounded-2xl border border-[#D8EBFF] bg-white px-3 py-2.5 shadow-[0_12px_28px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#0096FF]/45 xl:flex-col xl:items-start"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl" style={{ background: c.color }}>
                        <Icon size={18} weight="fill" className="text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-600">{c.label}</div>
                        <div className="font-display text-[13px] font-black leading-snug text-[#071126] transition-colors group-hover:text-[#0068B3] sm:text-[12px] lg:text-[13px]">{c.value}</div>
                      </div>
                    </a>
                  );
                })}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-[12px] font-black text-[#071126] sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
                {[
                  { icon: ShieldCheck, label: 'Secure booking' },
                  { icon: Lightning, label: 'Fast response' },
                  { icon: Headset, label: 'Human support' },
                  { icon: GlobeHemisphereEast, label: 'All travel needs' },
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center gap-2 rounded-full bg-[#EAF7FF] px-3 py-2">
                      <Icon size={15} weight="bold" className="shrink-0 text-[#0068B3]" />
                      <span className="leading-tight">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right - Form */}
            <div className="rounded-[26px] border border-[#BFE1FF] bg-white/95 p-4 shadow-[0_24px_70px_rgba(15,23,42,0.13)] backdrop-blur sm:p-5 lg:p-5">
              <div className="mb-4">
                <h3 className="font-display text-[22px] font-black leading-tight text-[#071126] sm:text-[25px]">
                  Get Your Free Consultation
                </h3>
                <div className="my-2.5 h-1 w-14 rounded-full bg-[#0096FF]" />
                <p className="text-[13px] font-semibold leading-relaxed text-slate-600">
                  Share your route, dates, budget, and passenger details. Our UAE-based team will reply with clear options.
                </p>
              </div>
              <LeadForm variant="consultation" layout="compactEnquiry" hideHeading />
            </div>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS -------------------------------------------------- */}
      <section className="py-20 px-6 lg:px-8" style={{ background: '#F5F5F5' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
            <div>
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">
                Traveller Reviews
              </span>
              <h2 className="font-display font-black text-[#071126]" style={{ fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)' }}>
                What our customers say.
              </h2>
            </div>
            <div className="flex gap-3">
              <button onClick={prevT} aria-label="Previous" className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-[#0096FF] hover:text-[#0068B3] transition-colors">
                <CaretLeft size={15} weight="bold" />
              </button>
              <button onClick={nextT} aria-label="Next" className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-[#0096FF] hover:text-[#0068B3] transition-colors">
                <CaretRight size={15} weight="bold" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <AnimatePresence mode="wait">
              {visibleT.map((t, i) => (
                <motion.div
                  key={`${testimonialIdx}-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ delay: i * 0.07, duration: 0.3 }}
                  className="bg-white rounded-2xl p-6 flex flex-col gap-4 shadow-sm"
                >
                  <div className="flex items-center gap-0.5">
                    {[...Array(t.stars)].map((_, s) => (
                      <Star key={s} size={14} weight="fill" style={{ color: '#FFB800' }} />
                    ))}
                  </div>
                  <Quotes size={24} weight="fill" style={{ color: '#0096FF', opacity: 0.15 }} />
                  <p className="text-gray-600 text-[13px] leading-relaxed font-medium flex-1">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-[#0077CC] flex items-center justify-center font-display font-black text-white text-[14px] shrink-0">
                      {t.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black text-[#071126] text-[13px] truncate">{t.name}</div>
                      <div className="text-[10px] font-black text-[#0068B3] uppercase tracking-wider truncate">{t.service}</div>
                      <div className="text-[10px] text-gray-600">{t.location}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Traveler count */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-3 bg-white rounded-xl px-5 py-3 shadow-sm border border-gray-100">
              <Users size={16} weight="fill" className="text-[#0068B3] shrink-0" />
              <div>
                <div className="font-black text-[#071126] text-[13px]">Trusted by UAE Travellers</div>
                <div className="text-[10px] text-gray-600">UAE residents &amp; nationals</div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <TrustpilotWidget />
          </div>
        </div>
      </section>

      {/* --- BLOG / GUIDES ------------------------------------------------- */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">
                Latest Travel Guides
              </span>
              <h2 className="font-display font-black text-[#071126]" style={{ fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)' }}>
                Fresh guides for UAE travellers &amp; companies.
              </h2>
            </div>
            <Link to="/blog" className="flex items-center gap-1 text-[#0068B3] font-black text-[11px] uppercase tracking-wider hover:gap-2 transition-all">
              All Guides <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <AnimatePresence mode="popLayout" initial={false}>
              {visibleGuides.map((g) => (
                <motion.div
                  key={g.slug}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45 }}
                >
                  <Link
                    to={g.href ?? `/blog/${g.slug}`}
                    className="group block h-full bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all"
                  >
                    <div className="h-44 overflow-hidden">
                      <img src={g.img} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                    </div>
                    <div className="p-4">
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#0068B3] bg-[#0096FF]/10 px-2.5 py-1 rounded-full mb-3 inline-block">
                        {g.cat}
                      </span>
                      <h3 className="font-display font-black text-[#071126] text-[13px] leading-snug group-hover:text-[#0068B3] transition-colors">
                        {g.title}
                      </h3>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          {GUIDES.length > GUIDE_WINDOW && (
            <div className="mt-8 flex justify-center gap-2">
              {GUIDES.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Show guides starting at ${i + 1}`}
                  onClick={() => setGuideStart(i)}
                  className={`h-1.5 rounded-full transition-all ${i === guideStart ? 'w-6 bg-[#0068B3]' : 'w-1.5 bg-gray-300 hover:bg-gray-400'}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection
        title="Ready to plan your next trip?"
        subtitle="Speak with a UAE-based travel expert today. Free consultation, no obligation quote within the hour."
        primaryLabel="WhatsApp Us"
        primaryHref={WA_BASE}
        secondaryLabel="Get Free Quote"
        secondaryHref="/contact"
        trust={["Visa assistance", "Air tickets", "Holiday packages", "UAE-based support"]}
      />
    </div>
  );
}
